import { type FC, useState, useEffect, useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { axios, type AxiosError } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { Presentation } from "./Presentation";
import { type FormValues } from "../../schema";

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  yearOptions: { label: string; value: number }[];
  fetchCityOptionsPath?: string;
  fetchPrefectureOptionsPath?: string;
};

export const ProfileFields: FC<Props> = ({
  yearOptions,
  fetchCityOptionsPath,
  fetchPrefectureOptionsPath,
}) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const sexValue = watch("sex");
  const birthYear = watch("birthYear");
  const birthMonth = watch("birthMonth");
  const birthDay = watch("birthDay");
  const prefectureValue = watch("addressPrefecture");
  const cityValue = watch("addressCity");
  const [prefectureList, setPrefectureList] = useState<Option[]>([]);
  const [cityList, setCityList] = useState<Option[]>([]);
  const [isLoadingPrefectures, setIsLoadingPrefectures] = useState<boolean>(false);
  const [isLoadingCities, setIsLoadingCities] = useState<boolean>(false);
  const prevPrefectureRef = useRef<string | undefined>(undefined);

  const fetchPrefectures = useCallback(() => {
    if (!fetchPrefectureOptionsPath) return;

    setIsLoadingPrefectures(true);
    axios
      .get(fetchPrefectureOptionsPath)
      .then((response) => {
        const prefecturesList: [string, string][] = response.data;
        const formattedPrefectureOptions = prefecturesList.map(([label, value]) => ({
          label,
          value,
        }));
        setPrefectureList(formattedPrefectureOptions);
      })
      .catch((error: AxiosError) => {
        sendErrorLog(new Error(error?.message || "都道府県のデータを取得できませんでした"));
        setPrefectureList([]);
      })
      .finally(() => {
        setIsLoadingPrefectures(false);
      });
  }, [fetchPrefectureOptionsPath]);

  const fetchCities = useCallback(
    (prefecture: string) => {
      if (!fetchCityOptionsPath) {
        if (!prefecture) setCityList([]);
        return;
      }

      setIsLoadingCities(true);
      axios
        .get(fetchCityOptionsPath, { params: { prefecture } })
        .then((response) => {
          const citiesList: [string, string][] = response.data;
          const formattedCityOptions = citiesList.map(([label, value]) => ({ label, value }));
          setCityList(formattedCityOptions);
        })
        .catch((error: AxiosError) => {
          sendErrorLog(new Error(error?.message || "市区町村のデータを取得できませんでした"));
          setCityList([]);
        })
        .finally(() => {
          setIsLoadingCities(false);
        });
    },
    [fetchCityOptionsPath],
  );

  useEffect(() => {
    if (fetchPrefectureOptionsPath) {
      fetchPrefectures();
    }
  }, [fetchPrefectures, fetchPrefectureOptionsPath]);

  useEffect(() => {
    if (prefectureValue) {
      if (fetchCityOptionsPath) {
        // 都道府県が変更された場合のみ市区町村をリセットする
        const isInitialMount = prevPrefectureRef.current === undefined;
        const isPrefectureChanged = prevPrefectureRef.current !== prefectureValue;

        if (!isInitialMount && isPrefectureChanged) {
          setValue("addressCity", "");
        }
        fetchCities(prefectureValue);
      }
    } else {
      setCityList([]);
    }
    prevPrefectureRef.current = prefectureValue;
  }, [fetchCities, prefectureValue, fetchCityOptionsPath, setValue]);

  const handleChangeSex = (value: string) => {
    setValue("sex", value);
  };

  const handleChangePrefecture = (value: string) => {
    setValue("addressPrefecture", value);
  };

  const handleChangeCity = (value: string) => {
    setValue("addressCity", value);
  };

  const handleChangeYear = (value: number) => {
    setValue("birthYear", value);
  };
  const handleChangeMonth = (value: number) => {
    setValue("birthMonth", value);
  };
  const handleChangeDate = (value: number) => {
    setValue("birthDay", value);
  };

  const stringifiedPrefectureOptions = prefectureList.map((p) => ({
    label: p.label,
    value: String(p.value),
  }));

  const stringifiedCityOptions = cityList.map((c) => ({
    label: c.label,
    value: String(c.value),
  }));

  return (
    <Presentation
      yearOptions={yearOptions}
      prefectureOptions={stringifiedPrefectureOptions}
      cityOptions={stringifiedCityOptions}
      sexValue={sexValue}
      prefectureValue={prefectureValue}
      cityValue={cityValue}
      yearValue={birthYear}
      monthValue={birthMonth}
      dateValue={birthDay}
      onChangeSex={handleChangeSex}
      onChangePrefecture={handleChangePrefecture}
      onChangeCity={handleChangeCity}
      onChangeYear={handleChangeYear}
      onChangeMonth={handleChangeMonth}
      onChangeDate={handleChangeDate}
      sexErrorMessage={errors.sex?.message}
      addressErrorMessage={errors.addressPrefecture?.message || errors.addressCity?.message}
      birthDateErrorMessage={
        errors.birthYear?.message || errors.birthMonth?.message || errors.birthDay?.message
      }
      isLoadingPrefectures={isLoadingPrefectures}
      isLoadingCities={isLoadingCities}
    />
  );
};
